<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { roundTo, toDate_wd_dd_MMMM, getSemiSuperficie, getSemiDateRécoltePrévue, getSemiVérifiéTextColor } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'

//https://stackoverflow.com/questions/57807226/vuetify-data-table-add-horizontal-stroller-bar



const theme = useTheme()
const storeU = useUtilisateurStore()
const router = useRouter()
const isLoading = ref(true)
const révision = ref('')

const semis = ref([] as MDBSemi[])
const buckets = ref([] as MDBVegcoBucket[])
const search = ref('')
const headers = ref<DataTableHeaders>([
  { title: '#', key: 'formattedNuméro', width: '120px' },
  { title: 'Champ', key: 'champ', width: '70px' },
  { title: 'Planches', key: 'planches', width: '130px' },
  { title: 'Date récolte prévue', key: 'dateRécoltePrévue2', width: '180px' },
  { title: 'bts/ac prévu', key: 'rendementPrévuBoîte', width: '80px' },
  { title: 'Nb. boîtes', key: 'nbBoîtes', width: '80px' },
  { title: 'Sup.', key: 'formattedSuperficie', width: '60px' },
  { title: 'Degré-jour', key: 'djPct', width: '170px' },
  { title: 'Variété', key: 'formattedVariété', width: '110px' },
  { title: 'Date plantation', key: 'formattedDateDu', width: '120px' },
  { title: 'Commentaires', key: 'cmnts' },
  { title: 'Vérifié', key: 'dateVérification', width: '150px' },
  { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
]);
const dialogColumns = ref(false)
const visibleColumns = ref(headers.value?.map(header => header.key).filter(key =>
  //key !== 'variété' &&
  (key !== 'djPct' || storeU.siteAAccès('degréjours'))
) ?? []);
const filteredHeaders = computed(() => headers.value?.filter(header => visibleColumns.value.includes(header.key)) ?? []);
const voirFermés = ref(false)
const showAjustementDJ = computed(() => storeU.siteAAccès('degréjours') &&
  (storeU.cultureSélectionnée.nom == 'Laitue Romaine' || storeU.cultureSélectionnée.nom == 'Iceberg / Pommée' || storeU.cultureSélectionnée.nom == 'Radis Chinois'))
const editAjustementDJ = ref(false)
const ajustementDJModifié = ref(0)
const isSavingAjustementDJ = ref(false)
const editDJNécessaireSemiId = ref<string | null>(null)
const savingDJNécessaireSemiId = ref<string | null>(null)
const djNécessaireManuelModifié = ref(0)
const unitParBoîte = computed(() => unitésParBoîte(storeU.cultureSélectionnée.nom))
const formattedSemis = computed(() => {
  let semisList = semis.value;

  if (!voirFermés.value) {
    semisList = semisList.filter(semi => !semi.fermé)
  }

  return semisList.map(semi => ({
    ...semi,
    formattedNuméro: semi.numéro + (semi.fermé ? ' (*)' : ''),
    formattedDateDu: toDate_wd_dd_MMMM(semi.dateDu),
    dateRécoltePrévue2: getSemiDateRécoltePrévue(semi),
    planches: semi.plancheDe + '-' + semi.plancheÀ + ' (' + (semi.plancheÀ - semi.plancheDe + 1) + ')',
    cmnts: getCommentaires(semi),
    formattedVariété: semi.variété + (semi.estBio ? ' (Bio)' : ''),
    rendementPrévuBoîte: semi.rendementPrévu / unitParBoîte.value,
    nbBoîtes: roundTo(getSemiSuperficie(semi) * semi.rendementPrévu / unitParBoîte.value, 0),
    formattedSuperficie: roundTo(getSemiSuperficie(semi), 2),
    djRéelAccumulé: roundTo(semi.degréJour?.djRéelAccumulé ?? 0, 0),
    djPct: semi.degréJour == null ? -1
      : semi.degréJour.complété ? 101
      : (semi.degréJour.djNécessaireCalculé == 0 && semi.degréJour.djNécessaireManuel == 0) ? -2
      : roundTo(semi.degréJour.djRéelAccumulé / (semi.degréJour.djNécessaireManuel !== 0 ? semi.degréJour.djNécessaireManuel : semi.degréJour.djNécessaireCalculé) * 100, 0),
  }));
});

function getDJColor(pct: number): string {
  if (pct >= 90) return 'success'
  if (pct >= 66) return 'orange'
  if (pct >= 33) return 'info'
  return 'primary'
}

function onAjustementDJCorbeilClick() {
  editAjustementDJ.value = false
  ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
}

async function onAjustementDJPencilClick() {
  if (isSavingAjustementDJ.value)
    return

  editAjustementDJ.value = !editAjustementDJ.value

  if (storeU.cultureSélectionnée.ajustementDJ != ajustementDJModifié.value) {
    try {
      isSavingAjustementDJ.value = true
      const siteCS = storeU.siteSélectionné.lettresDappel
      const config = { params: { culture: storeU.cultureSélectionnée.nom, ajustement: ajustementDJModifié.value } }
      await apiProphet.put(`sites/${siteCS}/degrejours/ajustementdj`, null, config)
      Notify.success('Ajustement DJ modifié')
      storeU.cultureSélectionnée.ajustementDJ = ajustementDJModifié.value
      await getSemis()
    } catch (e) {
      Notify.error(e)
    } finally {
      isSavingAjustementDJ.value = false
    }
  }
}

function onDJNécessairePencilClick(item: MDBSemi) {
  editDJNécessaireSemiId.value = item.id

  djNécessaireManuelModifié.value = 0
  if (item.degréJour != null) {
    if (item.degréJour.djNécessaireManuel != 0) {
      djNécessaireManuelModifié.value = item.degréJour.djNécessaireManuel
    } else {
      djNécessaireManuelModifié.value = roundTo(item.degréJour.djNécessaireCalculé, 0)
    }

    if (!item.degréJour.complété) {
      djNécessaireManuelModifié.value += ajustementDJModifié.value
    }
  }
}

function onDJNécessaireCancelClick() {
  editDJNécessaireSemiId.value = null
}

// Quick rendement edit
const editRendementSemiId = ref<string | null>(null)
const rendementModifié = ref(0)

function onRendementPencilClick(item: any) {
  editRendementSemiId.value = item.id
  rendementModifié.value = item.rendementPrévuBoîte
  nextTick(() => rendementInputRef.value?.focus())
}

const rendementInputRef = ref<HTMLInputElement | null>(null)

function onRendementCancelClick() {
  editRendementSemiId.value = null
}

async function saveRendement(item: any) {
  const newRendement = rendementModifié.value * unitParBoîte.value
  const semiToUpdate = semis.value.find(s => s.id === item.id)
  if (!semiToUpdate) return
  if (semiToUpdate.rendementPrévu === newRendement) {
    editRendementSemiId.value = null
    return
  }
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    await apiProphet.put(`sites/${siteCS}/semis/${item.id}/rendement`, null, { params: { rendement: newRendement } })
    semiToUpdate.rendementPrévu = newRendement
    Notify.success('Rendement modifié')
    editRendementSemiId.value = null
    await refreshRévision()
  } catch (e) {
    Notify.error(e)
  }
}

// Quick date récolte prévue edit
const editDateRécolteSemiId = ref<string | null>(null)
const dateRécolteModifiée = ref('')
const dateRécolteInputRef = ref<HTMLInputElement | null>(null)

function onDateRécoltePencilClick(item: any) {
  editDateRécolteSemiId.value = item.id
  // Format as YYYY-MM-DD for the date input
  const raw = item.dateRécoltePrévue2
  dateRécolteModifiée.value = raw ? raw.substring(0, 10) : ''
  nextTick(() => dateRécolteInputRef.value?.focus())
}

function onDateRécolteCancelClick() {
  editDateRécolteSemiId.value = null
}

async function saveDateRécolte(item: any) {
  if (editDateRécolteSemiId.value !== item.id) return
  editDateRécolteSemiId.value = null
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    await apiProphet.put(`sites/${siteCS}/semis/${item.id}/daterecolteprevuemanuelle`, null, { params: { dateRecolte: dateRécolteModifiée.value ? dateRécolteModifiée.value + 'T12:00:00' : '0001-01-01' } })
    const semiToUpdate = semis.value.find(s => s.id === item.id)
    if (semiToUpdate)
      semiToUpdate.dateRécoltePrévueManuelle = dateRécolteModifiée.value ? dateRécolteModifiée.value + 'T12:00:00' : ''
    Notify.success('Date de récolte prévue modifiée')
    await refreshRévision()
  } catch (e) {
    Notify.error(e)
  }
}

async function saveDJNécessaireManuel(item: MDBSemi) {
  if (savingDJNécessaireSemiId.value === item.id) return

  const semiToUpdate = semis.value.find(s => s.id === item.id)
  if (!semiToUpdate || !semiToUpdate.degréJour) return

  let newValue = djNécessaireManuelModifié.value
  if (!semiToUpdate.degréJour.complété)
    newValue -= ajustementDJModifié.value
  if (newValue < 0)
    newValue = 0

  if (semiToUpdate.degréJour.djNécessaireManuel === newValue) {
    editDJNécessaireSemiId.value = null
    return
  }

  try {
    savingDJNécessaireSemiId.value = item.id
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.put(`sites/${siteCS}/degrejours/miseajour/${item.id}`, null, {
      params: {
        djNecessaireManuel: newValue
      }
    })
    const semiApi = result.data as MDBSemi
    semiToUpdate.degréJour = semiApi.degréJour
    Notify.success('DJ nécessaire manuel modifié')
    editDJNécessaireSemiId.value = null
    await getSemis()
  } catch (e) {
    Notify.error(e)
  } finally {
    savingDJNécessaireSemiId.value = null
  }
}

function aDesBucketsAvecDesBalises(semi: MDBSemi) {
  return buckets.value.some(b => b.semiId == semi.id && b.balises.length > 0)
}

function getCommentaires(semi: MDBSemi) {
  let commentaires = ''

  if (semi.commentaires) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentaires
  }

  if (semi.commentairesFarmit) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentairesFarmit
  }

  for (const bucket of buckets.value.filter(b => b.semiId == semi.id)) {
    if (bucket.commentaires != undefined && bucket.commentaires != '') {
      if (commentaires != '') commentaires += ' ||| '
      commentaires += 'Bucket: ' + bucket.commentaires
    }
  }

  return commentaires
}

const totalSuperficie = computed(() => {
  return roundTo(semis.value.reduce((acc, semi) => acc + getSemiSuperficie(semi), 0), 2);
});

// Drag-selection for Sup. sum
const isDragging = ref(false)
const dragSelectedIds = ref<string[]>([])
const dragSelectionSum = computed(() => {
  if (dragSelectedIds.value.length === 0) return null
  const selected = semis.value.filter(s => dragSelectedIds.value.includes(s.id))
  return roundTo(selected.reduce((acc, s) => acc + getSemiSuperficie(s), 0), 2)
})

function startDrag(e: MouseEvent, item: any) {
  isDragging.value = true
  if (e.ctrlKey || e.metaKey) {
    // Ctrl+click: toggle individual row
    if (dragSelectedIds.value.includes(item.id))
      dragSelectedIds.value = dragSelectedIds.value.filter(id => id !== item.id)
    else
      dragSelectedIds.value = [...dragSelectedIds.value, item.id]
  } else {
    // Plain click / drag start: begin new selection
    dragSelectedIds.value = [item.id]
  }
}
function continueDrag(item: any) {
  if (!isDragging.value) return
  if (!dragSelectedIds.value.includes(item.id))
    dragSelectedIds.value = [...dragSelectedIds.value, item.id]
}
function endDrag() {
  isDragging.value = false
}
function getRowProps({ item }: { item: any }) {
  return {
    class: dragSelectedIds.value.includes(item.id) ? 'drag-row-selected' : '',
    style: 'user-select: none',
    onMousedown: (e: MouseEvent) => { e.preventDefault(); startDrag(e, item) },
    onMouseenter: () => continueDrag(item),
  }
}

function goToChampOnCarte(champ: string) {
  router.push({ path: '/carte', query: { champ } })
}

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.open()
  if (result != undefined && result != '') {
    storeU.sélectionnerCulture(result, null)
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
    await getSemis()
  } else {
    isLoading.value = false
  }
}

watch(() => storeU.siteSélectionné, async () => { await getSemis() })
watch(() => storeU.annéeSélectionnée, async () => { await getSemis() })

// Revision check
async function refreshRévision() {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
  révision.value = result.data
}

const intervalId = setInterval(async () => {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (storeU.cultureSélectionnée.nom == undefined || siteCS == undefined || siteCS == '') {
    return
  }
  const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
  if (révision.value != result.data) {
    getSemis()
  }
}, 3000)

async function getSemis() {
  isLoading.value = true
  semis.value = []

  if (storeU.cultureSélectionnée.nom == undefined) {
    Notify.info('Veuillez sélectionner une culture avant de continuer.')
    isLoading.value = false
    return
  }

  try {
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ

    const siteCS = storeU.siteSélectionné.lettresDappel

    const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
    révision.value = result.data

    const config = { params: { culture: storeU.cultureSélectionnée.nom, année: storeU.annéeSélectionnée } }
    const result2 = await apiProphet.get(`sites/${siteCS}/semis`, config)
    semis.value = result2.data

    const semiIds = semis.value.map((semi) => semi.id)
    const result3 = await apiProphet.post(`vgco/sites/${siteCS}/semis/multiple/buckets/commentaires`, semiIds)
    buckets.value = result3.data
  }
  catch (e) {
    Notify.error(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogSemi = useTemplateRef('dialogSemi')
async function _newSemi() {
  await dialogSemi.value?.openSemiNew()
  getSemis()
}
async function editSemi(semi: MDBSemi) {
  const modifs = await dialogSemi.value?.openSemiEdit(semi)
  if (modifs)
    getSemis()
}

/*const dialogDelete = useTemplateRef('dialogDelete')
async function deleteSemi(semi: MDBSemi) {
  if (semi.farmitId != undefined && semi.farmitId != '') {
    Notify.error('Impossible de supprimer un semi provenant de Farmit')
    return
  }

  const result = await dialogDelete.value?.open('Êtes-vous certain de vouloir supprimer ce semi ?');
  if (result?.valueOf()) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      await apiProphet.delete(`sites/${siteCS}/semis/${semi.id}`)
      Notify.success('Supprimé')
      await getSemis()
    } catch (e) {
      Notify.error(e)
    }
  }
}*/

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(semi: MDBSemi) {
  const modifs = await dialogBuckets.value?.openBucketsAsync(semi, null)
  if (modifs) {
    getSemis()
  }
}

function getVérifiéColumnValue(dateVérification: string) {
  if (dateVérification == undefined || dateVérification == null || dateVérification == '')
    return 'Jamais'
  else if (new Date(dateVérification).getFullYear() > 2000)
    return toDate_wd_dd_MMMM(dateVérification)
  else
    return 'Jamais'
}

function print() {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.open()
    printWindow.document.write(generateReportHTML())
    printWindow.document.close()
    //setTimeout(() => printWindow.print(), 500)
  }
}

function generateReportHTML() {
  const printableHeaders = filteredHeaders.value.filter(h => h.key !== 'actions')
  const rows = formattedSemis.value

  function getCellValue(key: string, item: any): string {
    switch (key) {
      case 'dateRécoltePrévue2':
        return item.dateRécoltePrévue2 == '' ? 'Aucune date' : toDate_wd_dd_MMMM(item.dateRécoltePrévue2)
      case 'dateVérification':
        return getVérifiéColumnValue(item.dateVérification)
      case 'djPct': {
        if (item.degréJour == null) return '⚠ Erreur'
        const djAccumulé = roundTo(item.degréJour.djRéelAccumulé, 0)
        const djNécessaire = roundTo(
          (item.degréJour.djNécessaireManuel != 0 ? item.degréJour.djNécessaireManuel : item.degréJour.djNécessaireCalculé)
          + (item.degréJour.complété ? 0 : ajustementDJModifié.value), 0)
        if (item.degréJour.complété)
          return `✓ ${djAccumulé} / ${djNécessaire}`
        if (item.degréJour.djNécessaireCalculé == 0 && item.degréJour.djNécessaireManuel == 0)
          return `${djAccumulé} / ⚠`
        const pct = roundTo(item.degréJour.djRéelAccumulé / item.degréJour.djNécessaireCalculé * 100, 0)
        return `${djAccumulé} / ${djNécessaire} (${pct}%)`
      }
      default:
        return String(item[key] ?? '')
    }
  }

  const now = new Date()
  const dateStr = `${now.toLocaleDateString('fr-CA')} ${now.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Semis - ${storeU.cultureSélectionnée.nom}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        h3 { margin-bottom: 4px; }
        p { margin: 0 0 12px; }
        table { border-collapse: collapse; width: 100%; }
        th { background-color: #f2f2f2; font-weight: bold; border: 1px solid #999; padding: 4px 6px; text-align: left; white-space: nowrap; }
        td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
        tr:nth-child(even) td { background-color: #fafafa; }
        .fermé td { color: #999; }
      </style>
    </head>
    <body>
      <h3>Semis — ${storeU.cultureSélectionnée.nom} ${storeU.annéeSélectionnée}</h3>
      <p>Total superficie : ${totalSuperficie.value}ac — Généré le : ${dateStr}</p>
      <table>
        <thead>
          <tr>
            ${printableHeaders.map(h => `<th>${h.title}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map(item => `
            <tr class="${item.fermé ? 'fermé' : ''}">
              ${printableHeaders.map(h => `<td>${getCellValue(String(h.key), item)}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body>
    </html>
  `
}

onMounted(async () => {
  window.addEventListener('mouseup', endDrag)
  if (storeU.cultureSélectionnée.id == undefined) {
    showDialogCulture()
  } else {
    await getSemis()
  }
})

onUnmounted(() => {
  window.removeEventListener('mouseup', endDrag)
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-4">

      <!-- Desktop -->
      <div v-if="$vuetify.display.mdAndUp" class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <!-- Left: title + culture + year -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Semis</div>
            <div class="text-caption text-medium-emphasis">Plantation par champ</div>
          </div>
          <v-chip
            color="primary" variant="tonal" size="large" class="ms-1 culture-chip"
            append-icon="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" style="cursor: pointer; font-weight: 600;"
            @click="showDialogCulture">
            {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
          </v-chip>
          <YearSelector/>
        </div>
        <!-- Right: stats + controls -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <span class="text-body-2 text-medium-emphasis">
            Total : <strong>{{ totalSuperficie }}ac</strong>
          </span>
          <v-chip
            v-if="dragSelectionSum !== null"
            closable color="blue" variant="tonal" size="small"
            @click:close="dragSelectedIds = []"
          >Sél. : {{ dragSelectionSum }}ac ({{ dragSelectedIds.length }} semis)</v-chip>
          <v-checkbox v-model="voirFermés" label="Fermés" class="ma-0 pa-0" hide-details density="compact"/>
          <v-divider vertical style="height: 28px; align-self: center;"/>
          <v-text-field
            v-model="search" prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" label="Recherche"
            density="comfortable" rounded="pill" variant="solo-filled" single-line hide-details flat
            style="min-width: 280px; max-width: 400px; flex: 1 1 280px;"
          />
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" @click="dialogColumns = true"/>
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z" @click="print()"/>
          <template v-if="showAjustementDJ">
            <v-divider vertical style="height: 28px; align-self: center;"/>
            <v-text-field
              v-if="!editAjustementDJ" v-model.number="ajustementDJModifié" label="Ajust. DJ" type="number"
              readonly :disabled="isSavingAjustementDJ" density="compact" variant="outlined" rounded="pill" hide-details style="max-width: 130px"
              append-inner-icon="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" @click:append-inner="onAjustementDJPencilClick"/>
            <v-text-field
              v-else v-model.number="ajustementDJModifié" label="Ajust. DJ" type="number"
              :disabled="isSavingAjustementDJ" density="compact" variant="outlined" rounded="pill" hide-details style="max-width: 160px"
              append-inner-icon="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" append-icon="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z"
              @click:append="onAjustementDJCorbeilClick" @click:append-inner="onAjustementDJPencilClick"/>
            <v-progress-circular
              v-if="isSavingAjustementDJ"
              indeterminate size="18" width="2" color="primary"/>
          </template>
        </div>
      </div>

      <!-- Mobile -->
      <template v-else>
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center" style="gap: 10px;">
            <v-avatar color="primary" variant="tonal" size="38" rounded="lg">
              <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="22"/>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Semis</div>
              <div class="text-caption text-medium-emphasis">{{ storeU.annéeSélectionnée }}</div>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 6px;">
            <v-chip
              color="primary" variant="tonal" size="small" class="culture-chip"
              append-icon="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" style="cursor: pointer; font-weight: 600;"
              @click="showDialogCulture">
              {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
            </v-chip>
            <YearSelector/>
          </div>
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <span class="text-caption text-medium-emphasis">
            Total : <strong>{{ totalSuperficie }}ac</strong>
          </span>
          <v-chip
            v-if="dragSelectionSum !== null"
            closable color="blue" variant="tonal" size="x-small"
            @click:close="dragSelectedIds = []"
          >Sél. : {{ dragSelectionSum }}ac</v-chip>
          <v-checkbox v-model="voirFermés" label="Fermés" class="ma-0 pa-0" hide-details density="compact"/>
          <v-spacer/>
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z" @click="print()"/>
        </div>
        <v-text-field
          v-model="search" prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" label="Rechercher un semi..."
          density="comfortable" rounded="pill" variant="solo-filled" single-line hide-details flat
          class="mt-3"
        />
      </template>

    </div>

    <v-divider/>

    <v-data-table
      :headers="filteredHeaders" :items="formattedSemis" :items-per-page="-1"
      :loading="isLoading" :search="search" item-key="id"
      :sort-by="[{ key: 'numéro', order: 'asc' }]" :sort-desc="false"
      :hide-default-header="$vuetify.display.smAndDown"
      :row-props="getRowProps">
      <template #item.formattedNuméro="{ value }">
        <v-chip color="primary" size="small" variant="flat" class="font-weight-bold">{{ value }}</v-chip>
      </template>
      <template #item.champ="{ value }">
        <v-chip color="teal" size="small" variant="tonal" class="font-weight-bold" style="cursor:pointer;" @click.stop="goToChampOnCarte(value)" @mousedown.stop>
          <v-icon start size="x-small">M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z</v-icon>
          {{ value }}
        </v-chip>
      </template>
      <template #item.planches="{ value }">
        <v-chip color="brown-lighten-1" size="small" variant="tonal" class="font-weight-bold">
          <v-icon start size="x-small">M22 20V4C22 2.9 21.1 2 20 2H4C2.9 2 2 2.9 2 4V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20M4 6.5V4H20V6.5H4M4 11V8.5H20V11H4M4 15.5V13H20V15.5H4M4 20V17.5H20V20H4Z</v-icon>
          {{ value }}
        </v-chip>
      </template>
      <template #item.dateRécoltePrévue2="{ value, item }">
        <template v-if="editDateRécolteSemiId !== item.id">
          <div class="rendement-cell d-inline-flex align-center">
            <v-chip
              color="green-darken-1" variant="tonal" size="small" style="cursor:text"
              @click="onDateRécoltePencilClick(item)" @mousedown.stop
            >
              <v-icon start size="x-small">M19,19H5V8H19M19,3H18V1H16V3H8V1H6V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M16.53,11.06L15.47,10L10.59,14.88L8.47,12.76L7.41,13.82L10.59,17L16.53,11.06Z</v-icon>
              {{ value == '' ? 'Aucune date' : toDate_wd_dd_MMMM(value) }}
            </v-chip>
            <v-icon class="rendement-pencil ml-1" size="x-small" @click="onDateRécoltePencilClick(item)" @mousedown.stop>M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
          </div>
        </template>
        <template v-else>
          <div class="d-flex align-center" style="gap:4px">
            <input
              ref="dateRécolteInputRef"
              v-model="dateRécolteModifiée"
              type="date"
              class="date-inline-input"
              @keyup.enter="saveDateRécolte(item)"
              @keyup.escape="onDateRécolteCancelClick"
              @blur="saveDateRécolte(item)"
              @mousedown.stop
              @click.stop
            />
          </div>
        </template>
      </template>
      <template #item.rendementPrévuBoîte="{ value, item }">
        <div class="d-flex align-center rendement-cell">
          <input
            v-if="editRendementSemiId === item.id"
            ref="rendementInputRef"
            v-model.number="rendementModifié"
            type="number"
            class="rendement-inline-input font-weight-bold text-primary no-spinner"
            @keyup.enter="saveRendement(item)"
            @keyup.escape="onRendementCancelClick"
            @blur="saveRendement(item)"
            @mousedown.stop
            @click.stop
          />
          <span v-else class="font-weight-bold text-primary" style="cursor:text" @click="onRendementPencilClick(item)" @mousedown.stop>{{ value }}</span>
          <v-icon
            class="rendement-pencil ml-1" size="x-small"
            @click="onRendementPencilClick(item)"
            @mousedown.stop
          >M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
        </div>
      </template>
      <template #item.nbBoîtes="{ value }">
        <span class="font-weight-bold text-success">{{ value }}</span>
      </template>
      <template #item.dateVérification="{ item }">
        <span :class="getSemiVérifiéTextColor(item)">{{ getVérifiéColumnValue(item.dateVérification) }}</span><br/>
        <v-chip v-if="!aDesBucketsAvecDesBalises(item)" size="x-small" color="error" variant="flat" class="mt-1">
          <v-icon start size="x-small">M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z</v-icon>
          Pas de balises
        </v-chip>
      </template>
      <template v-if="$vuetify.display.smAndDown" #item="props">
        <tr class="mobile-semi-row">
          <td class="pa-2" style="border-bottom: none;">
            <v-card
              class="semi-mobile-card"
              :class="props.item.fermé ? 'semi-mobile-card--fermé' : ''"
              :elevation="props.item.fermé ? 0 : 2"
              rounded="lg"
            >
              <!-- Header: # + Champ + Actions -->
              <div class="d-flex align-center justify-space-between px-3 pt-3 pb-1">
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <v-chip color="primary" size="small" variant="flat" class="font-weight-bold">
                    # {{ props.item.formattedNuméro }}
                  </v-chip>
                  <v-chip color="teal" size="small" variant="tonal" class="font-weight-bold" style="cursor:pointer;" @click.stop="goToChampOnCarte(props.item.champ)" @mousedown.stop>
                    <v-icon start size="x-small">M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z</v-icon>
                    {{ props.item.champ }}
                  </v-chip>
                  <v-chip color="brown-lighten-1" size="small" variant="tonal" class="font-weight-bold">
                    <v-icon start size="x-small">M22 20V4C22 2.9 21.1 2 20 2H4C2.9 2 2 2.9 2 4V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20M4 6.5V4H20V6.5H4M4 11V8.5H20V11H4M4 15.5V13H20V15.5H4M4 20V17.5H20V20H4Z</v-icon>
                    {{ props.item.planches }}
                  </v-chip>
                  <v-chip v-if="props.item.fermé" color="grey" size="x-small" variant="outlined">Fermé</v-chip>
                </div>
                <div class="d-flex">
                  <v-btn icon variant="text" size="small" density="compact" @click="editSemi(props.item)">
                    <v-icon size="20">M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
                  </v-btn>
                  <v-btn icon variant="text" size="small" density="compact" @click="openBuckets(props.item)">
                    <v-icon size="20">M3 4H21V7H20L17.5 21H6.5L4 7H3V4M17.97 7H6.03L8.15 19H15.85L17.97 7Z</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Date de récolte — accent info -->
              <div class="px-3 pb-2 d-flex align-center">
                <v-icon size="15" color="green-darken-1" class="mr-1">M19,19H5V8H19M19,3H18V1H16V3H8V1H6V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M16.53,11.06L15.47,10L10.59,14.88L8.47,12.76L7.41,13.82L10.59,17L16.53,11.06Z</v-icon>
                <span class="text-caption text-medium-emphasis mr-1">Récolte prévue :</span>
                <template v-if="editDateRécolteSemiId === props.item.id">
                  <input
                    ref="dateRécolteInputRef"
                    v-model="dateRécolteModifiée"
                    type="date"
                    class="date-inline-input"
                    @keyup.enter="saveDateRécolte(props.item)"
                    @keyup.escape="onDateRécolteCancelClick"
                    @blur="saveDateRécolte(props.item)"
                    @mousedown.stop
                    @click.stop
                  />
                </template>
                <template v-else>
                  <span
                    class="text-body-2 font-weight-medium"
                    style="cursor:text"
                    @click="onDateRécoltePencilClick(props.item)"
                  >
                    {{ props.item.dateRécoltePrévue2 === '' ? 'Aucune date' : toDate_wd_dd_MMMM(props.item.dateRécoltePrévue2) }}
                  </span>
                  <v-icon class="ml-1" size="x-small" color="grey" @click="onDateRécoltePencilClick(props.item)">M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
                </template>
              </div>

              <v-divider/>

              <!-- Key metrics: rendement / nb boîtes / superficie -->
              <div class="d-flex justify-space-around py-3">
                <div class="text-center">
                  <div class="rendement-cell rendement-cell--always-show d-inline-flex align-center justify-center">
                    <input
                      v-if="editRendementSemiId === props.item.id"
                      ref="rendementInputRef"
                      v-model.number="rendementModifié"
                      type="number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="rendement-inline-input font-weight-bold text-primary no-spinner text-h6"
                      style="width:5ch;text-align:center"
                      @keyup.enter="saveRendement(props.item)"
                      @keyup.escape="onRendementCancelClick"
                      @blur="saveRendement(props.item)"
                      @mousedown.stop
                      @click.stop
                    />
                    <span v-else class="text-h6 font-weight-bold text-primary" style="cursor:text" @click="onRendementPencilClick(props.item)" @mousedown.stop>{{ props.item.rendementPrévuBoîte }}</span>
                    <v-icon
                      class="rendement-pencil ml-1" size="x-small"
                      @click="onRendementPencilClick(props.item)"
                      @mousedown.stop
                    >M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
                  </div>
                  <div class="text-caption text-disabled">Rend./ac</div>
                </div>
                <v-divider vertical/>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold text-success">{{ props.item.nbBoîtes }}</div>
                  <div class="text-caption text-disabled">Boîtes</div>
                </div>
                <v-divider vertical/>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold">{{ props.item.formattedSuperficie }}</div>
                  <div class="text-caption text-disabled">Acres</div>
                </div>
              </div>

              <v-divider/>

              <!-- DJ progress (if available) -->
              <div v-if="props.item.degréJour != null && storeU.siteAAccès('degréjours')" class="px-3 pt-2 pb-1">
                <template v-if="props.item.degréJour.complété">
                  <v-progress-linear :model-value="100" color="grey" rounded height="14" bg-color="grey-lighten-1">
                    <span class="text-caption font-weight-bold">✓ Récolté</span>
                  </v-progress-linear>
                </template>
                <template v-else-if="props.item.degréJour.djNécessaireCalculé == 0 && props.item.degréJour.djNécessaireManuel == 0">
                  <v-progress-linear :model-value="0" color="warning" rounded height="14" bg-color="grey-lighten-1">
                    <span class="text-caption font-weight-bold">⚠ Cible DJ inconnue</span>
                  </v-progress-linear>
                </template>
                <template v-else>
                  <v-progress-linear
                    :model-value="Math.min(props.item.djPct, 100)"
                    :color="getDJColor(props.item.djPct)"
                    rounded height="14" bg-color="grey-lighten-1"
                  >
                    <span class="text-caption font-weight-bold dj-bar-text">{{ props.item.djPct }}% DJ</span>
                  </v-progress-linear>
                </template>
              </div>

              <!-- Secondary info -->
              <div class="px-3 py-2 text-caption">
                <div class="d-flex flex-wrap" style="gap: 2px 12px;">
                  <span><span class="text-medium-emphasis">Variété :</span> {{ props.item.formattedVariété }}</span>
                  <span><span class="text-medium-emphasis">Plantation :</span> {{ props.item.formattedDateDu }}</span>
                </div>
                <div v-if="props.item.cmnts" class="mt-1 text-medium-emphasis semi-cmnt-clamp">
                  <v-icon size="11" class="mr-1">M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9M10,16V19.08L13.08,16H20V4H4V16H10M6,7H18V9H6V7M6,11H15V13H6V11Z</v-icon>{{ props.item.cmnts }}
                </div>
              </div>

              <!-- Footer: vérification + balises -->
              <v-divider/>
              <div class="px-3 py-2 d-flex align-center justify-space-between">
                <span :class="['text-caption', getSemiVérifiéTextColor(props.item)]">
                  <v-icon size="12" class="mr-1">M23,12L20.56,9.22L20.9,5.54L17.29,4.72L15.4,1.54L12,3L8.6,1.54L6.71,4.72L3.1,5.53L3.44,9.21L1,12L3.44,14.78L3.1,18.47L6.71,19.29L8.6,22.47L12,21L15.4,22.46L17.29,19.28L20.9,18.46L20.56,14.78L23,12M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z</v-icon>
                  Vérifié : {{ getVérifiéColumnValue(props.item.dateVérification) }}
                </span>
                <v-chip v-if="!aDesBucketsAvecDesBalises(props.item)" size="x-small" color="error" variant="flat">
                  <v-icon start size="x-small">M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z</v-icon>
                  Pas de balises
                </v-chip>
              </div>
            </v-card>
          </td>
        </tr>
      </template>
      <template #item.formattedVariété="{ value, item }">
        <span>{{ value }}</span>
        <span v-if="item.estBio" class="text-caption">
          <v-icon size="small" color="green">M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z</v-icon>
        </span>
      </template>
      <template #item.djPct="{ item }">
        <!-- No DJ system -->
        <span v-if="item.degréJour == null">
          <v-icon>M23,12L20.56,9.22L20.9,5.54L17.29,4.72L15.4,1.54L12,3L8.6,1.54L6.71,4.72L3.1,5.53L3.44,9.21L1,12L3.44,14.78L3.1,18.47L6.71,19.29L8.6,22.47L12,21L15.4,22.46L17.29,19.28L20.9,18.46L20.56,14.78L23,12M13,17H11V15H13V17M13,13H11V7H13V13Z</v-icon>
          <v-tooltip activator="parent" location="bottom">
            N'a pas pu créer le système de degré-jour<br/>
            (Pas de date de récolte dans le passé ?)
          </v-tooltip>
        </span>
        <!-- Has DJ system -->
        <div v-else style="min-width:150px">
          <!-- Completed -->
          <template v-if="item.degréJour.complété">
            <v-progress-linear :model-value="100" color="grey" rounded height="18" bg-color="grey-lighten-1">
              <span class="text-caption font-weight-bold">✓ Récolté</span>
            </v-progress-linear>
          </template>
          <!-- No target -->
          <template v-else-if="item.degréJour.djNécessaireCalculé == 0 && item.degréJour.djNécessaireManuel == 0">
            <v-progress-linear :model-value="0" color="warning" rounded height="18" bg-color="grey-lighten-1">
              <span class="text-caption font-weight-bold">⚠ Cible inconnue</span>
            </v-progress-linear>
            <v-tooltip activator="parent" location="bottom">
              N'a pas pu trouvé de degré-jour nécessaire<br/>
              (Pas de données historique de degré-jour pour cette culture ?)
            </v-tooltip>
          </template>
          <!-- Normal progress -->
          <template v-else>
            <v-progress-linear
              :model-value="Math.min(item.djPct, 100)"
              :color="getDJColor(item.djPct)"
              rounded height="18" bg-color="grey-lighten-1"
            >
              <span class="text-caption font-weight-bold dj-bar-text">{{ item.djPct }}%</span>
            </v-progress-linear>
          </template>
          <!-- Accumulated / target row -->
          <div class="d-flex align-center mt-1 text-caption">
            <span>{{ roundTo(item.degréJour.djRéelAccumulé, 0) }}</span>
            <span class="mx-1">/</span>
            <template v-if="editDJNécessaireSemiId !== item.id">
              <span>{{ roundTo(item.degréJour.djNécessaireManuel != 0 ? item.degréJour.djNécessaireManuel : item.degréJour.djNécessaireCalculé, 0) + (item.degréJour.complété ? 0 : ajustementDJModifié) }}</span>
              <v-icon class="ml-1" size="x-small" @click="onDJNécessairePencilClick(item)" @mousedown.stop>M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
            </template>
            <template v-else>
              <v-text-field
                v-model.number="djNécessaireManuelModifié" type="number"
                density="compact" variant="outlined" hide-details
                :disabled="savingDJNécessaireSemiId === item.id"
                style="display:inline-block;max-width:80px;vertical-align:middle;"
                @keyup.enter="saveDJNécessaireManuel(item)"
                @mousedown.stop
              />
              <v-progress-circular
                v-if="savingDJNécessaireSemiId === item.id"
                class="ml-2"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
              <template v-else>
                <v-icon class="ml-1" size="small" color="success" @click="saveDJNécessaireManuel(item)" @mousedown.stop>M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z</v-icon>
                <v-icon size="small" color="error" @click="onDJNécessaireCancelClick" @mousedown.stop>M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z</v-icon>
              </template>
            </template>
          </div>
        </div>
      </template>
      <template #item.actions="{ item }">
        <v-btn icon variant="text" size="small" density="compact" @click="editSemi(item)" @mousedown.stop>
          <v-icon size="18">M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z</v-icon>
        </v-btn>
        <v-btn icon variant="text" size="small" density="compact" @click="openBuckets(item)" @mousedown.stop>
          <v-icon size="18">M3 4H21V7H20L17.5 21H6.5L4 7H3V4M17.97 7H6.03L8.15 19H15.85L17.97 7Z</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>

  <DialogCulture ref='dialogCulture'/>
  <DialogConfirm ref="dialogDelete"/>
  <DialogColonnes ref='dialogColonnes' v-model="dialogColumns" v-model:headers="headers" v-model:visibleheaders="visibleColumns" />
  <DialogSemi ref='dialogSemi'/>
  <DialogBuckets ref="dialogBuckets"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Culture chip hover ───────────────────── */
.culture-chip {
  transition: opacity 0.15s;
}
.culture-chip:hover {
  opacity: 0.78;
}

:deep(.drag-row-selected td) {
  background-color: rgba(33, 150, 243, 0.15) !important;
}
:deep(.no-spinner input[type=number]::-webkit-inner-spin-button),
:deep(.no-spinner input[type=number]::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
:deep(.no-spinner input[type=number]) {
  -moz-appearance: textfield;
}
:deep(.rendement-edit-field .v-field__append-inner) {
  padding-top: 0;
  align-items: center;
  gap: 2px;
}
.rendement-inline-input {
  width: 4.5ch;
  background: transparent;
  border: none;
  border-bottom: 1.5px solid currentColor;
  outline: none;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  padding: 0;
  color: rgb(var(--v-theme-primary));
  -moz-appearance: textfield;
}
.rendement-inline-input::-webkit-inner-spin-button,
.rendement-inline-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.date-inline-input {
  background: transparent;
  border: none;
  border-bottom: 1.5px solid rgb(var(--v-theme-primary));
  outline: none;
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  padding: 0;
  cursor: pointer;
}
.rendement-cell:not(:hover) .rendement-pencil {
  opacity: 0;
}
.rendement-cell:hover .rendement-pencil,
.rendement-cell--always-show .rendement-pencil {
  opacity: 0.6;
}
.dj-bar-text {
  color: #1a1a1a;
  text-shadow:
    -1px -1px 0 rgba(255,255,255,0.9),
     1px -1px 0 rgba(255,255,255,0.9),
    -1px  1px 0 rgba(255,255,255,0.9),
     1px  1px 0 rgba(255,255,255,0.9);
}

/* Mobile card styles */
:deep(.mobile-semi-row) {
  background: transparent !important;
}
:deep(.mobile-semi-row:hover td) {
  background: transparent !important;
}
.semi-mobile-card {
  border-left: 3px solid rgb(var(--v-theme-primary));
  transition: box-shadow 0.2s;
}
.semi-mobile-card--fermé {
  border-left-color: #bdbdbd;
  opacity: 0.75;
}
.semi-cmnt-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
